const clampScore = (value) => Math.min(10, Math.max(-10, value));

export function calculateModaScores({
  annualizedROI,
  riskScores,
  ltrTotalROIAnnualized,
  annualCashFlow,
  initialInvestment,
  strTotalROIAnnualized,
  annualStrCashFlow,
  objectiveWeights,
}) {
  const totalWeight = Object.values(objectiveWeights).reduce((sum, w) => sum + w, 0);
  const objectiveCount = Object.keys(objectiveWeights).length;
  const normalizedWeights = {};
  Object.entries(objectiveWeights).forEach(([key, value]) => {
    // All sliders at zero means no stated preference: weight every objective equally.
    normalizedWeights[key] = totalWeight > 0 ? (value / totalWeight) * 100 : 100 / objectiveCount;
  });

  // One common scale for every strategy: 20% a year = 10/10 and 25%
  // cash-on-cash = 10/10. Losses score negative, so a money-losing strategy
  // cannot win on its fixed risk and workload scores alone.
  //
  // The return scored is each strategy's yearly growth over the same five
  // years from the same starting cash (dealAnalysis `sameHorizon`). A flip's
  // own annualized ROI assumes it could be repeated back to back all year.
  const roiScore = (annualizedRoiPct) => clampScore(annualizedRoiPct / 2);
  const cashFlowScore = (annualCash) => {
    if (!(initialInvestment > 0)) return 0;
    return clampScore(((annualCash / initialInvestment) * 100) / 2.5);
  };

  const scores = {
    flip: {
      roi: roiScore(annualizedROI),
      cashFlow: 0, // flips have no ongoing cash flow
      risk: riskScores.flip,
      workload: 3,
    },
    ltr: {
      roi: roiScore(ltrTotalROIAnnualized),
      cashFlow: cashFlowScore(annualCashFlow),
      risk: riskScores.ltr,
      workload: 7,
    },
    str: {
      roi: roiScore(strTotalROIAnnualized),
      cashFlow: cashFlowScore(annualStrCashFlow),
      risk: riskScores.str,
      workload: 2,
    },
  };

  const weightedScores = { flip: 0, ltr: 0, str: 0 };

  Object.entries(scores).forEach(([strategy, strategyScores]) => {
    Object.entries(strategyScores).forEach(([objective, score]) => {
      weightedScores[strategy] += (score * normalizedWeights[objective]) / 100;
    });
  });

  return { scores, weightedScores };
}

// `eligible` excludes strategies the user gave no inputs for (e.g. no rent entered).
export function getRecommendedStrategy(weightedScores, eligible = {}) {
  const candidates = Object.entries(weightedScores).filter(([strategy]) => eligible[strategy] !== false);
  const pool = candidates.length > 0 ? candidates : Object.entries(weightedScores);
  return pool
    .reduce((best, [strategy, score]) => (score > best.score ? { strategy, score } : best), { strategy: pool[0][0], score: -Infinity })
    .strategy;
}
